import * as yup from 'yup';
import type { FieldPath } from 'react-hook-form';
import type { ProductCatalog } from '@/interfaces/strapi';

/** --------- Types del formulario --------- */
export type BookItem = {
	id: number | string;
	title: string;
	quantity: number;
};
export type PhoneSplit = {
	country: string;
	number: string;
};

export type CatalogRequestFormValues = {
	// Step 1
	name: string;
	nif: string;
	email: string;
	movil: PhoneSplit;

	// Step 2
	direccion: string;
	direccionFacturacion?: string;
	libros: BookItem[];

	// Step 3
	acceptPolicy: string[];
};

const NIF_REGEX = /^[0-9XYZxyz]\d{7}[A-Za-z]$/;
const PHONE_NUMBER_REGEX = /^\d{4,15}$/;

export function combinePhone({ country, number }: PhoneSplit): string {
	const cc = String(country || '').replace(/[^\d+]/g, '');
	const num = String(number || '').replace(/\D/g, '');
	const dial = cc.startsWith('+') ? cc : `+${cc}`;
	return `${dial}${num}`;
}

export const formSchema = yup.object({
	// Paso 1
	name: yup.string().trim().required('El nombre es obligatorio'),
	nif: yup.string().trim().matches(NIF_REGEX, 'NIF/NIE no válido').required('El NIF/NIE es obligatorio'),
	email: yup.string().trim().email('Email no válido').required('El email es obligatorio'),
	movil: yup
		.object({
			country: yup
				.string()
				.trim()
				.matches(/^\+?\d{1,4}$/, 'Prefijo inválido')
				.required('El prefijo es obligatorio'),
			number: yup
				.string()
				.trim()
				.matches(PHONE_NUMBER_REGEX, 'Introduce solo dígitos (4–15)')
				.required('El número es obligatorio'),
		})
		.test('movil-e164', 'Teléfono inválido', (value) => {
			if (!value) return false;
			const full = combinePhone(value);
			return /^\+\d{5,19}$/.test(full);
		}),

	// Paso 2
	direccion: yup.string().trim().required('La dirección es obligatoria'),
	direccionFacturacion: yup.string().trim().optional(),
	libros: yup
		.array()
		.of(
			yup.object({
				id: yup.mixed<number | string>().required('ID de libro obligatorio'),
				title: yup.string().trim().required('Título obligatorio'),
				quantity: yup
					.number()
					.typeError('Cantidad inválida')
					.integer('Cantidad entera')
					.min(1, 'Mínimo 1')
					.required('La cantidad es obligatoria'),
			}),
		)
		.min(1, 'Debes añadir al menos un libro'),

	// Paso 3
	acceptPolicy: yup.array(yup.string()).min(1, 'Debes aceptar la política de protección de datos'),
});

export const stepFields: Array<Array<FieldPath<CatalogRequestFormValues>>> = [
	['name', 'nif', 'email', 'movil.country', 'movil.number'],
	['direccion', 'direccionFacturacion', 'libros'],
	['acceptPolicy'],
];

export function buildDefaultValues(defaultProduct?: ProductCatalog): CatalogRequestFormValues {
	const firstBook = defaultProduct
		? { id: defaultProduct.id ?? defaultProduct.slug, title: defaultProduct.name, quantity: 1 }
		: undefined;

	return {
		name: '',
		nif: '',
		email: '',
		movil: { country: '+34', number: '' },
		direccion: '',
		direccionFacturacion: '',
		libros: firstBook ? [firstBook] : [],
		acceptPolicy: [],
	};
}
